<template>
    <WorkScheduleMarker
        class="deployment-work-schedule-assignment"
        draggable="true"
        @dragstart="handleDragStart"
        @dragend="handleDragEnd"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
        :style="itemStyle"
        :fulfillmentRequirement="workSchedulePosition.fulfillmentRequirement"
        :title="workSchedulePosition.name"
    >
        <span class="name">{{ workSchedulePosition.name }}</span>
    </WorkScheduleMarker>
</template>

<script lang="ts">
    import {CSSProperties} from 'vue';
    import {WorkScheduleAssignmentDragData} from '@/types';
    import {PlanbordWorkSchedulePosition, PlanbordWorkScheduleGroupLabel} from '@/types/api-object-extensions';
    import {WorkScheduleGridPosition} from '@/types/types';
    import WorkScheduleUtil from '@/util/WorkScheduleUtil';
    import WorkScheduleMarker from '@/components/WorkSchedule/WorkScheduleMarker.vue';
    import TooltipWorkSchedule from '@/components/WorkSchedule/Tooltip/TooltipWorkSchedule.vue';
    import {WorkScheduleAssignment} from '@cquential/api';
    import {ColorUtil} from '@cquential/ui';
    import {Component, Vue, Prop} from 'vue-facing-decorator';

    @Component({
        components: {
            WorkScheduleMarker,
        },
        emits: [
            'dragStart:workScheduleAssignment',
            'dragEnd:workScheduleAssignment',
        ],
    })
    export default class DeploymentWorkScheduleAssignment extends Vue {
        @Prop({required: true}) public deploymentId!: number;
        @Prop({required: true}) public beginDateTimeDeployment!: Date;
        @Prop({required: true}) public endDateTimeDeployment!: Date;
        @Prop({required: true}) public groupLabels!: Record<number, PlanbordWorkScheduleGroupLabel>;
        @Prop({required: true}) public workScheduleAssignment!: WorkScheduleAssignment;
        @Prop({required: true}) public workSchedulePosition!: PlanbordWorkSchedulePosition;

        public get itemStyle(): CSSProperties {
            const position: WorkScheduleGridPosition = WorkScheduleUtil.getPositionInHours(this.beginDateTimeDeployment, this.workScheduleAssignment.beginDateTime, this.workScheduleAssignment.endDateTime);

            const css: CSSProperties = {
                '--offset-left': 'calc(' + position.left + ' * var(--hour-cell-width))',
                '--width': 'calc(' + position.width + ' * var(--hour-cell-width))',
            };

            const groupHexColor: string | null = this.groupLabels[this.workSchedulePosition.workScheduleGroupLabelId!].color;
            if (groupHexColor && ColorUtil.isValidHexColor(groupHexColor)) {
                css.backgroundColor = this.groupLabels[this.workSchedulePosition.workScheduleGroupLabelId!].color!;
                css.color = ColorUtil.getTextContrastColor(this.groupLabels[this.workSchedulePosition.workScheduleGroupLabelId!].color!);
            }

            return css;
        }

        public handleMouseEnter(event: MouseEvent): void {
            /** @see TooltipWorkSchedule.openTooltip() */
            // @ts-expect-error TS2339
            TooltipWorkSchedule.__vfdConstructor.openTooltip(event.target! as HTMLElement, this.workSchedulePosition, this.workScheduleAssignment);
        }

        public handleMouseLeave(event: MouseEvent): void {
            /** @see TooltipWorkSchedule.closeTooltip() */
            // @ts-expect-error TS2339
            TooltipWorkSchedule.__vfdConstructor.closeTooltip(event.relatedTarget === null ? undefined : event.relatedTarget as HTMLElement);
        }

        public handleDragStart(event: DragEvent): void {
            /** @see TooltipWorkSchedule.closeTooltip() */
            // @ts-expect-error TS2339
            TooltipWorkSchedule.__vfdConstructor.closeTooltip();
            const data: WorkScheduleAssignmentDragData = {
                workScheduleAssignmentId: this.workScheduleAssignment.id!,
            };
            event.dataTransfer!.setData('text', JSON.stringify(data));
            this.$emit('dragStart:workScheduleAssignment', this.workScheduleAssignment);
        }

        public handleDragEnd(): void {
            this.$emit('dragEnd:workScheduleAssignment', this.workScheduleAssignment);
        }
    }
</script>

<style lang="scss" scoped>
    .deployment-work-schedule-assignment {
        position: absolute;
        left: var(--offset-left);
        width: var(--width);
        height: 100%;
        display: flex;
        padding: 0 var(--cell-padding);
        justify-content: space-between;
        box-sizing: border-box;
        background: var(--work-schedule-assigned-background);
        border-left: 1px solid var(--work-schedule-assigned-border);
        border-right: 1px solid var(--work-schedule-assigned-border);
        cursor: pointer;

        .name {
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
        }
    }
</style>
